import { rollbarInstance } from "../app/utils/rollbar";

/**
 * Attaches the auth token to every outgoing request.
 *
 * @param {import("axios").AxiosInstance} axios
 * @param {import("redux").Store} store
 */
export default function setupAxios(axios, store) {
  axios.interceptors.request.use(
    (config) => {
      const {
        auth: { authToken },
      } = store.getState();

      if (authToken) {
        config.headers.Authorization = `${authToken}`;
      }

      return config;
    },
    (err) => Promise.reject(err)
  );
}

/**
 * Installs a response interceptor: redirects to /logout on 401 and reports
 * other non-cancelled failures to Rollbar with anonymised context only.
 *
 * @param {import("axios").AxiosInstance} axios
 * @param {(path: string) => void} navigate
 * @param {Function} setSnack
 */
export function setupAxiosResponse(axios, navigate, setSnack) {
  axios.interceptors.response.use(
    function(response) {
      return response;
    },
    function(error) {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      const status = error?.response?.status;

      if (status === 401) {
        navigate("/logout");
        return Promise.reject(error);
      }

      // 400 (bad request) and 422 (validation/business-rule rejection) are
      // expected, caller-handled responses — not frontend defects — so they
      // are not reported to Rollbar.
      if (status === 400 || status === 422) {
        return Promise.reject(error);
      }

      const safeError = new Error(error?.message?.slice(0, 200) || "API error");
      safeError.name = error?.name || "AxiosError";
      safeError.stack = error?.stack;

      rollbarInstance?.error(safeError, {
        urlPath: error?.config?.url?.split("?")[0],
        method: error?.config?.method,
        status,
        statusText: error?.response?.statusText,
      });

      return Promise.reject(error);
    }
  );
}
